// LIVE-SAVE-1 (docs/specs/live-savings-1.md §4): swap an ALREADY-MIXED live session (one that
// will get the full-file batch pass anyway) to record-only after its captions have gone
// unwatched for the grace period, and back to live the moment they're watched again. A fully
// live session is never auto-switched (spec §0, decided 2026-09-24) — but only if the user wants live (they were in live when we swapped, and
// haven't since chosen otherwise) and hasn't pinned "Keep live while away".
//
// Ownership rule: the hook only ever undoes ITS OWN swap. Any mode change the user makes goes
// through `setModeByUser`, which clears the auto state, so a user who deliberately picks
// Record-only is never flipped back to live behind their back.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TranscriptionMode } from '@scribe/core/types';
import type { StreamingStatus } from './useStreamingSession';
import {
  DEFAULT_AUTO_SWITCH_CONFIG,
  unwatchedGraceMs,
  type AutoSwitchConfig,
} from '../lib/liveAutoSwitch';

export interface UseLiveAutoSwitchArgs {
  enabled: boolean;
  status: StreamingStatus;
  mode: TranscriptionMode;
  setMode: (mode: TranscriptionMode, reason?: 'auto') => void;
  watched: boolean;
  /** Mirrors `useStreamingSession().batchPassExpected` — false = fully live = never swap. */
  batchPassExpected: boolean;
  /** User override: never auto-swap this session. */
  alwaysLive: boolean;
  config?: AutoSwitchConfig;
}

export interface UseLiveAutoSwitch {
  /** True while the session is in record-only because WE swapped it. */
  autoSwitched: boolean;
  /** The user's own toggle — use this instead of `setMode` for manual changes. */
  setModeByUser: (mode: TranscriptionMode) => void;
}

export function useLiveAutoSwitch({
  enabled,
  status,
  mode,
  setMode,
  watched,
  batchPassExpected,
  alwaysLive,
  config = DEFAULT_AUTO_SWITCH_CONFIG,
}: UseLiveAutoSwitchArgs): UseLiveAutoSwitch {
  const [autoSwitched, setAutoSwitched] = useState(false);
  const autoRef = useRef(false);

  const markAuto = useCallback((value: boolean) => {
    autoRef.current = value;
    setAutoSwitched(value);
  }, []);

  // A finished/failed session starts the next one clean.
  useEffect(() => {
    if (status !== 'live' && status !== 'stopping' && autoRef.current) markAuto(false);
  }, [status, markAuto]);

  // Unwatched, already-mixed live session -> record-only after the grace.
  const graceMs = unwatchedGraceMs(config, batchPassExpected);
  useEffect(() => {
    if (graceMs === null) return; // fully live: never auto-switched
    if (!enabled || alwaysLive || status !== 'live' || mode !== 'streaming' || watched) return;
    const timer = setTimeout(() => {
      markAuto(true);
      setMode('batch', 'auto');
    }, graceMs);
    return () => clearTimeout(timer);
  }, [enabled, alwaysLive, status, mode, watched, graceMs, setMode, markAuto]);

  // Watched again (or the user pinned live) -> back to live, immediately. No grace on the
  // way back: the whole point is that captions are there the moment someone looks.
  useEffect(() => {
    if (!autoRef.current || status !== 'live' || mode !== 'batch') return;
    if (watched || alwaysLive || !enabled) {
      markAuto(false);
      setMode('streaming', 'auto');
    }
  }, [enabled, watched, alwaysLive, status, mode, setMode, markAuto]);

  const setModeByUser = useCallback(
    (newMode: TranscriptionMode) => {
      markAuto(false);
      setMode(newMode);
    },
    [setMode, markAuto],
  );

  return { autoSwitched, setModeByUser };
}
